import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";

// The server's limit of Test.heading.
export const HEADING_LIMIT = 120;
// The counter shows only near the limit.
const COUNT_FROM = HEADING_LIMIT - 20;

// What the server refused in the heading (400 {heading: [..]}), as one line. Its one rule is the length, and DRF's
// message names the limit in the server's language (English for Kazakh): that one is said in the page's language,
// anything else as the server words it.
const MENTIONS_LIMIT = new RegExp(`(^|\\D)${HEADING_LIMIT}(\\D|$)`);
export const headingErrorText = (t, messages) => {
  const text = [].concat(messages || []).filter((m) => typeof m === "string").join(" ");
  return MENTIONS_LIMIT.test(text) ? t("test_bank.form.heading_too_long", { max: HEADING_LIMIT }) : text;
};

/**
 * A test's page heading (Test.heading), in the create forms and the builder's Edit test: printed at the top of the
 * test's pages in the exam's Word; left empty, the Word takes the subject's printed title, else its name. Optional.
 * `onChange` gets the input's event (its name is "heading"); `error`: what the server refused (400 {heading}).
 * Typing stops at the limit (maxLength, as the server counts or stricter): the counter turns amber there, and a
 * screen reader is told (a longer paste is cut there silently).
 */
const TestHeadingField = forwardRef(function TestHeadingField({ id, value, onChange, error = "", disabled = false }, ref) {
  const [t] = useTranslation();
  const count = value.length;
  const near = count >= COUNT_FROM;
  const full = count >= HEADING_LIMIT;
  const describedBy = [error && `${id}-error`, `${id}-hint`, near && `${id}-count`].filter(Boolean).join(" ");
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
        {t("test_bank.form.heading")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
      </label>
      <input
        ref={ref}
        id={id}
        name="heading"
        type="text"
        value={value}
        onChange={onChange}
        maxLength={HEADING_LIMIT}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`block h-10 w-full rounded-lg px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 max-sm:h-11 ${
          error
            ? "border-red-300 hover:border-red-400 focus:border-red-500 focus:ring-red-500/15"
            : "border-slate-300 hover:border-slate-400 focus:border-blue-600 focus:ring-blue-600/15"
        }`}
      />
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      )}
      {/* Phones: the counter under the hint, which keeps its width (beside it, it would wrap to twice the lines). */}
      <div className="mt-1.5 flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <p id={`${id}-hint`} className="min-w-0 text-xs text-slate-500">
          {t("test_bank.form.heading_hint")}
        </p>
        {near && (
          <p
            id={`${id}-count`}
            className={`shrink-0 self-end text-xs tabular-nums sm:self-auto ${full ? "font-medium text-amber-700" : "text-slate-500"}`}
          >
            {count}
            {t("question_detail.comment.of_max_characters", { max: HEADING_LIMIT })}
          </p>
        )}
      </div>
      {/* Always there, so that the limit is announced when it is reached. */}
      <span role="status" className="sr-only">
        {full ? t("test_bank.form.heading_limit_reached", { max: HEADING_LIMIT }) : ""}
      </span>
    </div>
  );
});

export default TestHeadingField;
